import './index.scss';

import React from 'react';
import { connect } from 'react-redux';
import { Tabs, Divider, Skeleton, Tooltip } from 'antd';
import { format as formatDate } from 'date-fns';
import { TeamOutlined } from '@ant-design/icons';
import { User } from '../../types/user';
import { Service } from '../../types/service';
import getNested from '../../utils/getNested';
import { getUserFullName } from '../../utils/bookingUtil';
import { StylistType } from '../../types/stylist';

const { TabPane } = Tabs;


export enum LayoutType {
  Tabs = 'tabs',
  List = 'list',
};

interface Props {
  users: User[];
  primaryColor: string;
  skeleton?: boolean;
  layout?: LayoutType;
  activeUserId?: number;
  isAdmin: boolean;
  storeActiveUser?: (userId: number) => void;
}

class BookingDetailHeader extends React.PureComponent<Props> {
  private validateServices(user: User) {
    const services = getNested(user, 'selectedServices', []);
    return services.length > 0;
  }

  private validateStylists(user: User) {
    const services = getNested(user, 'selectedServices', []);
    const hasInvalidStylist = services.find((service: Service) => service.stylistId === StylistType.NoUser);
    return !hasInvalidStylist;
  }

  private validateDateTime(user: User) {
    const { selectedDate, selectedTime } = user;
    return !!selectedDate && !!selectedTime;
  }

  private validateTab(index: number) {
    const { users } = this.props;
    const validateUsers = users.slice(0, index);
    let validateMessage = '';

    const hasInvalidUser = validateUsers.find(prevUser => {
      const isValidServices = prevUser.canSelectService
        ? this.validateServices(prevUser) : true;
      const isValidStylists = prevUser.canSelectStylist
        ? isValidServices && this.validateStylists(prevUser)
        : true;
      const isValidDateTime = prevUser.canSelectDateTime
        ? isValidServices && isValidStylists && this.validateDateTime(prevUser)
        : true;

      if (!isValidServices) {
        validateMessage = `Please select services for ${getUserFullName(prevUser)} to continue`;
        return true;
      } else if (!isValidStylists) {
        validateMessage = `Please select stylists for ${getUserFullName(prevUser)} to continue`;
        return true;
      } else if (!isValidDateTime) {
        validateMessage = `Please select date & time for ${getUserFullName(prevUser)} to continue`;
        return true;
      }

      return false;
    });

    return {
      disabled: !!hasInvalidUser,
      message: validateMessage,
    };
  }

  renderTabTooltip(tabName: string | React.ReactNode, message: string) {
    return (
      <Tooltip
        placement="bottom"
        title={message}
      ><div>{tabName}</div></Tooltip>
    );
  }

  getTabPaneProps(user: User, index: number) {
    const { users, activeUserId } = this.props;
    const activeUserIndex = users.findIndex(user => user.id === activeUserId);
    let tabName = `${users.length > 1 ? `${index + 1}. ` : ''}${getUserFullName(user)}`;

    if (index > 0 && index !== activeUserIndex) {
      const tabValidation = this.validateTab(index);
      if (tabValidation.disabled) {
        return {
          key: user.id,
          disabled: true,
          tab: this.renderTabTooltip(tabName, tabValidation.message),
        } as any;
      }
    }

    return {
      tab: <div>{tabName}</div>,
      key: user.id,
    } as any;
  }

  onTabClick(tabKey: string) {
    const { storeActiveUser } = this.props;

    if (storeActiveUser) {
      storeActiveUser(Number(tabKey));
    }
  }

  getDateTime() {
    const { users } = this.props;
    const { selectedDate, selectedTime } = (users || [])[0];

    return {
      date: selectedDate ? formatDate(selectedDate, 'EEEE, MMM dd yyyy') : undefined,
      time: selectedTime,
    };
  }

  renderServices(user: User, services: Service[]) {
    const { skeleton, primaryColor } = this.props;
    return (
      <div className="user-booking-summary--services">
        {skeleton && [1, 2].map(() => (
          <div
            className="user-booking-summary--service"
            key={`user-${user.id}-booking-summary--service--${Math.random()}`}
          >
            <div className="user-booking-summary--service-name" style={{ marginBottom: 10 }}>
              <div style={{ width: '40%', height: 16, overflow: 'hidden' }}><Skeleton.Input size="small" /></div>
            </div>
            <div className="user-booking-summary--service-stylist">
              <div style={{ width: '70%', height: 16, overflow: 'hidden' }}><Skeleton.Input size="small" /></div>
            </div>
          </div>
        ))}

        {!skeleton && services.length > 0 && services.map(service => (
          <div
            className="user-booking-summary--service"
            key={`user-${user.id}-booking-summary--service-${service.key}-${Math.random()}`}
            style={{ color: primaryColor }}
          >
            <div className="user-booking-summary--service-name">
              {service.name}
            </div>
            <div className="user-booking-summary--service-stylist">
              {user.canSelectStylist ? service.stylistName : 'No Preference'}
            </div>
          </div>
        ))}

        {(skeleton || services.length > 1) && (
          <div className="user-booking-summary--services-sept"></div>
        )}
      </div>
    );
  }

  renderTabContent(user: User) {
    const dateTime = this.getDateTime();
    const { skeleton, primaryColor } = this.props;
    const userServices = getNested(user, 'selectedServices', []);

    return (
      <div className="user-booking-summary">
        {!skeleton && (
          <div className="user-booking-summary--date-time">
            <span className="user-booking-summary--date" style={{ color: dateTime.date ? primaryColor : undefined }}>
              {dateTime.date || 'Date'}
            </span> | <span style={{ color: dateTime.time ? primaryColor : undefined }}>
              {dateTime.time || 'Time'}
            </span>
          </div>
        )}
        {skeleton && (
          <div className="user-booking-summary--date-time" style={{ height: 20, overflow: 'hidden' }}>
            <Skeleton.Input size="small" />
          </div>
        )}

        {this.renderServices(user, userServices)}
      </div>
    );
  }

  render() {
    const {
      users,
      activeUserId,
      primaryColor,
      layout = LayoutType.Tabs,
      isAdmin,
    } = this.props;

    if (layout === LayoutType.Tabs) {
      return (
        <div className={`booking-header--heading ${!isAdmin ? 'tabs--no-padding' : ''}`}>
          {isAdmin && (
            <div className="booking-header--heading-users">
              <TeamOutlined
                style={{ fontSize: 20, marginRight: 4 }}
              /> <span
                style={{ fontSize: 16, position: 'relative', top: 1 }}
              >{users.length}</span>
            </div>
          )}

          <Tabs
            type="card"
            className="booking-header--users"
            activeKey={activeUserId ? String(activeUserId) : undefined}
            onTabClick={tabKey => this.onTabClick(tabKey)}
            tabBarGutter={10}
          >
            {users.map((user, index) => {
              return (
                <TabPane {...this.getTabPaneProps(user, index)}>
                  {this.renderTabContent(user)}
                </TabPane>
              );
            })}
          </Tabs>
        </div>
      );
    }

    const dateTime = this.getDateTime();
    return (
      <>
        <div className="booking-header--heading" style={{ minHeight: 40 }}>
          <div className="booking-header--heading-users">
            {isAdmin && (
              <>
                <TeamOutlined
                  style={{ fontSize: 20, marginRight: 4 }}
                /> <span
                  style={{ fontSize: 16, position: 'relative', top: 1 }}
                >{users.length}</span> &nbsp; - &nbsp; <b>Booking summary</b>
              </>
            )}

            {!isAdmin && <b>Booking summary</b>}
          </div>
        </div>

        <div className="user-booking-summary" style={{ background: '#ffffff', padding: 16 }}>
          <div className="user-booking-summary--date-time">
            <span className="user-booking-summary--date" style={{ color: dateTime.date ? primaryColor : undefined }}>
              {dateTime.date || 'Date'}
            </span> | <span style={{ color: dateTime.time ? primaryColor : undefined }}>
              {dateTime.time || 'Time'}
            </span>
          </div>

          <div className="user-booking-summary--users" style={{ paddingTop: 10 }}>
            {users.map((user, index) => {
              const userServices = getNested(user, 'selectedServices', []);
              const fullName = `${users.length > 1 ? `${index + 1}. ` : ''}${getUserFullName(user)}`;

              return (
                <div key={`user-booking-summary--list-user-${user.id}-${Math.random()}`}>
                  {index > 0 && <Divider style={{ marginBottom: 10, marginTop: 10 }} />}

                  <div className="user-booking-summary--user">
                    <h3 style={{ marginBottom: -5 }}>{fullName}</h3>
                    {this.renderServices(user, userServices)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = state => ({
  isAdmin: state.salonStore.isAdmin,
  users: state.bookingStore.users,
  primaryColor: state.salonStore.primaryColor,
});

export default connect(mapStateToProps)(BookingDetailHeader);
